/**
 * @fileoverview `<PlateInput>` — saisie plaque Côte d'Ivoire.
 *
 * Le portail V1 expose la recherche par plaque comme seul point d'entrée
 * visible tant que la recherche LIQ reste derrière feature flag. Ce composant
 * normalise la saisie vers `AB572GG` et affiche `AB 572 GG`.
 *
 * @see Documents/Paymetrust_Guide_Integration_Frontend_v1.pdf §4.3
 */

import { forwardRef } from 'react';
import { cn } from '../lib/utils.ts';

export interface PlateInputProps {
  /** Valeur canonique, ex. `AB572GG`. */
  value?: string;
  /** Reçoit la valeur canonique uppercase, sans espaces, tronquée à 7 caractères. */
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  name?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  className?: string;
}

/**
 * Normalise une plaque CI vers `AA000AA`.
 *
 * @param value - Chaîne saisie par l'utilisateur.
 * @returns Valeur uppercase alphanumérique sans espaces.
 */
export function normalizePlate(value: string): string {
  return value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 7);
}

/**
 * Formate une plaque CI pour l'affichage.
 *
 * @param value - Valeur canonique ou libre.
 * @returns Valeur groupée, ex. `AB 572 GG`.
 */
export function formatPlate(value: string): string {
  const normalized = normalizePlate(value);
  return [normalized.slice(0, 2), normalized.slice(2, 5), normalized.slice(5, 7)]
    .filter(Boolean)
    .join(' ');
}

/**
 * Champ de plaque CI contrôlé.
 *
 * @param props - Valeur canonique, handler, attributs ARIA et classe CSS.
 * @returns Input texte mobile-friendly avec formatage visuel.
 *
 * @example
 * ```tsx
 * <PlateInput value={plate} onChange={setPlate} />
 * ```
 */
export const PlateInput = forwardRef<HTMLInputElement, PlateInputProps>(function PlateInput(
  {
    value = '',
    onChange,
    placeholder = 'AB 572 GG',
    disabled = false,
    readOnly = false,
    className,
    ...props
  },
  ref,
): React.JSX.Element {
  const invalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true';

  return (
    <input
      ref={ref}
      type="text"
      inputMode="text"
      autoCapitalize="characters"
      autoComplete="off"
      value={formatPlate(value)}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
      id={props.id}
      name={props.name}
      className={cn(
        'border-input bg-background text-quipux-navy-950 flex h-12 w-full min-w-0 rounded-md border px-4 font-mono text-lg font-semibold uppercase tracking-normal outline-none',
        'placeholder:text-muted-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        invalid &&
          'border-danger ring-danger/20 focus-visible:border-danger focus-visible:ring-danger/20',
        className,
      )}
      onChange={(event) => onChange?.(normalizePlate(event.target.value))}
    />
  );
});
